<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AgentsAPI from 'dashboard/api/agents';
import TaccCompanyAPI from 'dashboard/api/taccCompany';
import TaccRemindersAPI from 'dashboard/api/taccReminders';
import TaccWorkItemsAPI from 'dashboard/api/taccWorkItems';
import AsanaTaskModal from 'dashboard/components/widgets/conversation/asana/AsanaTaskModal.vue';
import ReminderModal from 'dashboard/components/widgets/conversation/reminders/ReminderModal.vue';
import TaccWorkItemCard from './TaccWorkItemCard.vue';
import TaccWorkItemsFilters from './TaccWorkItemsFilters.vue';
import TaccWorkItemsSummary from './TaccWorkItemsSummary.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: '',
  },
});

const items = ref([]);
const agents = ref([]);
const companies = ref([{ value: '', label: 'All companies' }]);
const isLoading = ref(false);
const isLoadingAgents = ref(false);
const errorMessage = ref('');
const asanaModalRef = ref(null);
const reminderModalRef = ref(null);
const activeTask = ref(null);
const activeReminderConversationId = ref('');
const filters = reactive({
  search: '',
  company_name: '',
  agent_id: '',
  status: props.type === 'tasks' ? '' : 'scheduled',
});

const emptyTitle = computed(() =>
  props.type === 'tasks' ? 'No tasks found' : 'No reminders found'
);

const itemLabel = computed(() => (props.type === 'tasks' ? 'tasks' : 'reminders'));
const activeCompanyLabel = computed(() => filters.company_name || 'All companies');
const companyOptions = computed(() => companies.value);
const normalizeAgent = agent => {
  const source = agent.user || agent;
  return {
    id: source.id || agent.user_id,
    name: source.available_name || source.name || source.email,
    email: source.email,
  };
};
const agentOptions = computed(() => [
  { value: '', label: 'All agents' },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);
const activeAgentLabel = computed(() => {
  const selected = agentOptions.value.find(
    option => String(option.value || '') === String(filters.agent_id || '')
  );
  return selected?.label || 'All agents';
});
const statusOptions = computed(() => {
  const taskStatuses = [
    { value: '', label: 'All statuses' },
    { value: 'open', label: 'In progress' },
    { value: 'done', label: 'Done' },
  ];
  const reminderStatuses = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'all', label: 'All statuses' },
    { value: 'notified', label: 'Notified' },
    { value: 'canceled', label: 'Canceled' },
  ];
  return props.type === 'tasks' ? taskStatuses : reminderStatuses;
});
const activeStatusLabel = computed(() => {
  const selected = statusOptions.value.find(
    option => String(option.value || '') === String(filters.status || '')
  );
  return selected?.label || 'All statuses';
});

const updateFilters = nextFilters => {
  Object.assign(filters, nextFilters);
};

const loadCompanies = async () => {
  const { data } = await TaccCompanyAPI.getBusinesses();
  companies.value = [
    { value: '', label: 'All companies' },
    ...(data.businesses || []).map(company => ({
      value: company.name,
      label: company.name,
    })),
  ];
};

const loadAgents = async () => {
  isLoadingAgents.value = true;
  try {
    const { data } = await AgentsAPI.get();
    const payload = Array.isArray(data)
      ? data
      : data.payload || data.agents || [];
    agents.value = payload.map(normalizeAgent).filter(agent => agent.id);
  } catch {
    agents.value = [];
  } finally {
    isLoadingAgents.value = false;
  }
};

const loadItems = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const params = {
      search: filters.search || undefined,
      company_name: filters.company_name || undefined,
      agent_id: filters.agent_id || undefined,
      status: filters.status || undefined,
    };
    const { data } =
      props.type === 'tasks'
        ? await TaccWorkItemsAPI.getTasks(params)
        : await TaccWorkItemsAPI.getReminders(params);
    items.value = props.type === 'tasks' ? data.tasks || [] : data.reminders || [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || error.message || 'Could not load items.';
  } finally {
    isLoading.value = false;
  }
};

const removeItem = deletedItem => {
  items.value = items.value.filter(item => item.id !== deletedItem?.id);
};

const onItemSaved = async () => {
  activeTask.value = null;
  await loadItems();
};

const conversationDisplayId = item => item.conversation?.display_id;

const editItem = item => {
  if (props.type === 'tasks') {
    activeTask.value = item;
    asanaModalRef.value?.open('manage', item);
    return;
  }

  activeReminderConversationId.value = conversationDisplayId(item);
  if (!activeReminderConversationId.value) {
    useAlert('Could not find the reminder conversation.');
    return;
  }
  reminderModalRef.value?.open('edit', item);
};

const deleteReminder = async item => {
  const displayId = conversationDisplayId(item);
  if (!displayId) {
    useAlert('Could not find the reminder conversation.');
    return;
  }

  if (!window.confirm(`Delete reminder "${item.title}"?`)) return;

  try {
    await TaccRemindersAPI.deleteReminder(displayId, item.id);
    removeItem(item);
    useAlert('Reminder deleted.');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Could not delete reminder.');
  }
};

watch(filters, loadItems);

onMounted(async () => {
  await Promise.allSettled([loadCompanies(), loadItems(), loadAgents()]);
});
</script>

<template>
  <main class="flex h-full min-h-0 flex-1 flex-col bg-n-surface-1">
    <header class="sticky top-0 z-20 px-6">
      <div class="mx-auto flex w-full max-w-5xl flex-col gap-4 py-6">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h1 class="m-0 truncate text-xl font-medium text-n-slate-12">
              {{ title }}
            </h1>
            <p v-if="subtitle" class="m-0 mt-1 text-sm text-n-slate-11">
              {{ subtitle }}
            </p>
          </div>
          <Button
            color="slate"
            size="sm"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            @click="loadItems"
          />
        </div>

        <div class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3 shadow-sm">
          <TaccWorkItemsFilters
            :type="type"
            :companies="companyOptions"
            :agents="agentOptions"
            :loading-agents="isLoadingAgents"
            :status-options="statusOptions"
            :filters="filters"
            @update:filters="updateFilters"
          />
          <TaccWorkItemsSummary
            :count="items.length"
            :item-label="itemLabel"
            :company-label="activeCompanyLabel"
            :agent-label="activeAgentLabel"
            :status-label="activeStatusLabel"
          />
        </div>
      </div>
    </header>

    <section class="min-h-0 flex-1 overflow-auto px-6 pb-6">
      <div class="mx-auto w-full max-w-5xl">
        <div v-if="isLoading" class="flex h-48 items-center justify-center">
          <Spinner />
        </div>

        <div
          v-else-if="errorMessage"
          class="flex h-48 flex-col items-center justify-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 text-center"
        >
          <p class="m-0 text-sm text-n-ruby-10">{{ errorMessage }}</p>
          <Button xs outline color="slate" label="Retry" @click="loadItems" />
        </div>

        <div
          v-else-if="!items.length"
          class="flex h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak bg-n-solid-1 text-center"
        >
          <span
            class="size-7 text-n-slate-10"
            :class="type === 'tasks' ? 'i-lucide-list-checks' : 'i-lucide-clock-3'"
          />
          <p class="m-0 text-sm font-medium text-n-slate-12">{{ emptyTitle }}</p>
        </div>

        <div v-else class="flex flex-col gap-4">
          <TaccWorkItemCard
            v-for="item in items"
            :key="item.id"
            :item="item"
            :type="type"
            @edit="editItem"
            @delete="deleteReminder"
          />
        </div>
      </div>
    </section>

    <AsanaTaskModal
      v-if="type === 'tasks'"
      ref="asanaModalRef"
      :conversation-id="activeTask?.conversation?.display_id || ''"
      :task="activeTask"
      @saved="onItemSaved"
      @deleted="removeItem"
    />

    <ReminderModal
      v-if="type === 'reminders'"
      ref="reminderModalRef"
      :conversation-id="activeReminderConversationId"
      :agents="agents"
      :loading-agents="isLoadingAgents"
      @saved="onItemSaved"
    />
  </main>
</template>
